/**
 * 测试用例列表组件
 */

import React, { useCallback } from 'react';
import type { TestCase, TestCaseResult } from '../../types/testTypes';

// ============================================
// 类型定义
// ============================================

export interface TestCaseListProps {
  /** 测试用例列表 */
  readonly testCases: readonly TestCase[];
  /** 当前选中的用例 ID */
  readonly selectedId: string | null;
  /** 测试结果映射 */
  readonly results: Readonly<Record<string, TestCaseResult>>;
  /** 是否正在运行 */
  readonly isRunning: boolean;
  /** 选中回调 */
  readonly onSelect: (id: string) => void;
  /** 添加回调 */
  readonly onAdd: () => void;
  /** 删除回调 */
  readonly onDelete: (id: string) => void;
  /** 复制回调 */
  readonly onDuplicate: (id: string) => void;
  /** 运行回调 */
  readonly onRun: (id: string) => void;
}

// ============================================
// 子组件：用例状态图标
// ============================================

interface StatusIconProps {
  readonly status: TestCase['status'];
}

const StatusIcon: React.FC<StatusIconProps> = ({ status }) => {
  switch (status) {
    case 'passed':
      return <i className="fa fa-check-circle plc-test-icon-passed" />;
    case 'failed':
      return <i className="fa fa-times-circle plc-test-icon-failed" />;
    case 'running':
      return <i className="fa fa-spinner fa-spin plc-test-icon-running" />;
    case 'error':
      return <i className="fa fa-exclamation-circle plc-test-icon-error" />;
    default:
      return <i className="fa fa-circle-o plc-test-icon-pending" />;
  }
};

// ============================================
// 子组件：用例列表项
// ============================================

interface TestCaseItemProps {
  readonly testCase: TestCase;
  readonly isSelected: boolean;
  readonly isRunning: boolean;
  readonly onSelect: () => void;
  readonly onDelete: () => void;
  readonly onDuplicate: () => void;
  readonly onRun: () => void;
}

const TestCaseItem: React.FC<TestCaseItemProps> = ({
  testCase,
  isSelected,
  isRunning,
  onSelect,
  onDelete,
  onDuplicate,
  onRun
}) => {
  // 阻止事件冒泡
  const handleActionClick = useCallback(
    (e: React.MouseEvent, action: () => void): void => {
      e.stopPropagation();
      action();
    },
    []
  );
  
  const itemClassName = [
    'plc-test-case-item',
    isSelected ? 'selected' : '',
    testCase.status
  ].filter(Boolean).join(' ');
  
  return (
    <div
      className={itemClassName}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelect();
        }
      }}
    >
      <div className="plc-test-case-info">
        <StatusIcon status={testCase.status} />
        <span className="plc-test-case-name" title={testCase.name}>
          {testCase.name}
        </span>
      </div>
      
      <div className="plc-test-case-actions">
        <button
          type="button"
          className="plc-test-case-action"
          title="运行此用例"
          onClick={(e) => handleActionClick(e, onRun)}
          disabled={isRunning}
        >
          <i className="fa fa-play" />
        </button>
        <button
          type="button"
          className="plc-test-case-action"
          title="复制用例"
          onClick={(e) => handleActionClick(e, onDuplicate)}
          disabled={isRunning}
        >
          <i className="fa fa-copy" />
        </button>
        <button
          type="button"
          className="plc-test-case-action plc-test-case-action-danger"
          title="删除用例"
          onClick={(e) => handleActionClick(e, onDelete)}
          disabled={isRunning}
        >
          <i className="fa fa-trash" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// 主组件：测试用例列表
// ============================================

export const TestCaseList: React.FC<TestCaseListProps> = ({
  testCases,
  selectedId,
  results,
  isRunning,
  onSelect,
  onAdd,
  onDelete,
  onDuplicate,
  onRun
}) => {
  return (
    <div className="plc-test-case-list">
      <div className="plc-test-case-list-header">
        <span>测试用例 ({testCases.length})</span>
        <button
          type="button"
          className="plc-test-btn plc-test-btn-sm"
          onClick={onAdd}
          disabled={isRunning}
          title="添加用例"
        >
          <i className="fa fa-plus" />
        </button>
      </div>
      
      <div className="plc-test-case-list-body">
        {testCases.map((testCase) => (
          <TestCaseItem
            key={testCase.id}
            testCase={testCase}
            isSelected={testCase.id === selectedId}
            isRunning={isRunning}
            onSelect={() => onSelect(testCase.id)}
            onDelete={() => onDelete(testCase.id)}
            onDuplicate={() => onDuplicate(testCase.id)}
            onRun={() => onRun(testCase.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default TestCaseList;